import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { useChat } from '@ai-sdk/react';
import { StickToBottom } from 'use-stick-to-bottom';

import { Box, Button, Flex, Text, Theme } from 'baukasten';

import { useToasts } from 'features/toaster/useToasts';

import { useCompanionWorkspaceContext } from './CompanionWorkspaceContext';
import { AnchorToSentMessage } from './components/AnchorToSentMessage';
import {
  ChatComposer,
  type ComposerSubmission,
} from './components/ChatComposer';
import { ChatMessage } from './components/ChatMessage';
import { ThinkingBlock } from './components/ThinkingBlock';
import { ThreadHistory } from './components/ThreadHistory';
import { WelcomeState } from './components/WelcomeState';
import messages from './messages';
import { useCompanionProfile } from './hooks/useCompanionProfile';
import { useCompanionTransport } from './hooks/useCompanionTransport';
import { useSubjectPrompt } from './hooks/useSubjectPrompt';
import { useTurnStartedAt } from './hooks/useTurnStartedAt';
import type { ActionSpec } from './types';
import { toFileParts } from './utils/fileParts';
import { chosenActions } from './utils/messageParts';

const Scroller = styled(StickToBottom)`
  flex: 1;
  min-height: 0;
  overflow-y: hidden;
`;

const Content = styled(StickToBottom.Content)`
  padding: ${Theme.getBaseUnit(4)};
`;

// the history panel only covers the chat visually; `inert` also takes it out of the tab
// order (React 18 has no typing for it, the empty string sets the attribute)
const inertWhile = (covered: boolean) => ({ inert: covered ? '' : undefined });

export const NativeChat = () => {
  const { formatMessage } = useIntl();
  const { showErrorToast } = useToasts();
  const { chatId, initialMessages, isHistoryOpen, refreshThreads, starters } =
    useCompanionWorkspaceContext();
  const [preparing, setPreparing] = useState(false);
  const { data: profile } = useCompanionProfile();
  const transport = useCompanionTransport();

  const {
    messages: turns,
    sendMessage,
    stop,
    status,
    error,
  } = useChat({
    transport,
    id: chatId,
    messages: initialMessages,
    onFinish: refreshThreads,
  });

  // retry replays the turn's options too, so voice, subject and action turns stay labelled
  const lastTurnOptions = useRef<Parameters<typeof sendMessage>[1]>();
  const sendTurn: typeof sendMessage = (message, options) => {
    lastTurnOptions.current = options;
    return sendMessage(message, options);
  };

  const isResponding = status === 'submitted' || status === 'streaming';
  const busy = preparing || isResponding;
  const turnStartedAt = useTurnStartedAt(isResponding);
  const actionsEnabled = !preparing && status === 'ready';
  const picked = chosenActions(turns);
  const lastMessage = turns.at(-1);
  const lastUserMessage = [...turns]
    .reverse()
    .find(message => message.role === 'user');
  const awaitingAssistantMessage =
    isResponding && lastMessage?.role !== 'assistant';

  // encoding attachments runs before useChat knows about the turn; hold the composer meanwhile
  const send = async ({ text, files, isVoiceOnly }: ComposerSubmission) => {
    setPreparing(true);
    try {
      const fileParts = files.length ? await toFileParts(files) : undefined;
      sendTurn(
        { text, files: fileParts },
        isVoiceOnly ? { body: { input_type: 'voice' } } : undefined
      );
    } catch (encodeError) {
      showErrorToast(formatMessage(messages.turnFailed));
      throw encodeError;
    } finally {
      setPreparing(false);
    }
  };

  useSubjectPrompt((text, subject) =>
    sendTurn({ text }, { body: { subject } })
  );

  const runAction = (action: ActionSpec) => {
    if (!actionsEnabled) return;
    sendTurn(
      { text: action.payload },
      { body: { action_id: crypto.randomUUID(), action_label: action.label } }
    );
  };

  // a failed turn may already be stored, so resend it as a new turn rather than replace it
  const retry = () => {
    if (lastUserMessage)
      sendMessage({ parts: lastUserMessage.parts }, lastTurnOptions.current);
  };

  // the disclaimer travels with the composer so the first message sees it too
  const composer = (
    <Box>
      <ChatComposer
        key={chatId}
        busy={busy}
        onSubmit={send}
        onStop={isResponding ? stop : undefined}
      />
      {profile?.disclaimer && (
        <Flex mt={1} justifyContent="center">
          <Text.SmallDescription color="grey-dark">
            {profile.disclaimer}
          </Text.SmallDescription>
        </Flex>
      )}
    </Box>
  );

  if (!turns.length && !error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        minHeight="0"
        position="relative"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <Flex fullWidth justifyContent="center" {...inertWhile(isHistoryOpen)}>
          <WelcomeState
            starters={starters}
            composer={composer}
            onPick={text => sendTurn({ text })}
          />
        </Flex>
        {isHistoryOpen && <ThreadHistory />}
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      height="100%"
      minHeight="0"
      position="relative"
    >
      <Scroller
        role="log"
        aria-label={formatMessage(messages.conversation)}
        initial="smooth"
        resize="smooth"
        {...inertWhile(isHistoryOpen)}
      >
        <Content>
          {turns.map((message, index) => (
            <ChatMessage
              key={message.id}
              message={message}
              isLast={message === lastMessage}
              turnStartedAt={turnStartedAt}
              streaming={status === 'streaming'}
              actionsEnabled={actionsEnabled}
              chosenAction={picked[index]}
              onAction={runAction}
            />
          ))}
          {awaitingAssistantMessage && (
            <ThinkingBlock steps={[]} active turnStartedAt={turnStartedAt} />
          )}
          {error && (
            <Flex alignItems="center" gap={2}>
              <Text fontSize={14} color="red-dark">
                {formatMessage(messages.turnFailed)}
              </Text>
              {lastUserMessage && (
                <Button type="button" variant="text" size="s" onClick={retry}>
                  {formatMessage(messages.turnRetry)}
                </Button>
              )}
            </Flex>
          )}
        </Content>
        <AnchorToSentMessage
          lastUserMessageId={lastUserMessage?.id}
          justSent={lastMessage?.role === 'user'}
        />
      </Scroller>
      <Box px={4} pb={4} {...inertWhile(isHistoryOpen)}>
        {composer}
      </Box>
      {isHistoryOpen && <ThreadHistory />}
    </Box>
  );
};
